<template>
  <v-fade-transition leave-absolute>
    <div
      v-if="!parentLoading"
      class="d-flex flex-column"
      style="gap: 8px"
    >
      <div
        class="d-flex align-center justify-space-between"
        style="gap: 24px; min-height: 44px"
      >
        <div
          v-if="title"
          class="text-body-1 font-weight-black primary--text d-flex grow"
        >
          {{ title }}
        </div>
        <div
          class="d-flex align-center justify-end shrink"
          style="gap: 12px"
        >
          <v-menu
            v-if="!mobileSearch && filterable"
            key="filter"
            offset-y
            :nudge-bottom="8"
            content-class="rounded-lg"
          >
            <template #activator="{ on, attrs, value }">
              <v-chip
                v-if="!mobileSearch"
                v-bind="attrs"
                :class="{ 'v-chip--active': !!currentTab }"
                text-color="review"
                :outlined="!!currentTab"
                class="rounded-pill text-caption text-lg-body-2 grow d-flex align-center justify-center font-weight-bold"
                style="
                  gap: 6px;
                  padding-right: 10px;
                  min-height: 28px !important;
                "
                :style="filterBackgroundColor"
                v-on="$vuetify.breakpoint.smAndDown ? on : ''"
                @click="
                  $vuetify.breakpoint.smAndDown
                    ? ''
                    : (showFilters = !showFilters)
                "
              >
                <div
                  class="d-flex align-center justify-center"
                  style="gap: 6px"
                >
                  <v-icon
                    :size="16"
                    color="review"
                  >
                    mdi-filter-variant
                  </v-icon>
                  <v-fade-transition
                    leave-absolute
                    hide-on-leave
                    group
                    :duration="150"
                    tag="div"
                    class="d-flex align-center justify-center"
                    style="gap: 4px"
                  >
                    <span
                      v-if="!!currentTab"
                      key="value"
                    >1</span>
                    <span key="label">Filter</span>
                    <v-icon
                      v-if="currentTab"
                      key="closeBtn"
                      small
                      color="review"
                      class="ml-1"
                      @click.stop="currentTab = null"
                    >
                      mdi-close-circle
                    </v-icon>
                    <v-icon
                      v-if="!currentTab"
                      key="menuIcon"
                      size="20"
                      color="review"
                    >
                      {{
                        showFilters || value ? "mdi-menu-up" : "mdi-menu-down"
                      }}
                    </v-icon>
                  </v-fade-transition>
                </div>
              </v-chip>
            </template>
            <v-list
              dense
              content-class="rounded-lg"
              class="pt-0"
            >
              <v-subheader
                class="font-weight-bold justify-center"
                style="height: 30px"
              >
                Status
              </v-subheader>
              <v-list-item-group v-model="currentTab">
                <v-list-item
                  v-for="(item, index) in getOrderStatus"
                  :key="index"
                  :value="item.value"
                  :dense="$vuetify.breakpoint.smAndDown"
                  :style="{
                    minHeight: $vuetify.breakpoint.smAndDown ? '35px' : '40px',
                  }"
                >
                  <v-list-item-title class="d-flex align-center">
                    {{
                      item.text
                    }}
                  </v-list-item-title>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
          <v-fade-transition
            tag="div"
            class="d-flex align-center justify-end shrink"
            style="gap: 12px; transition: all 0.15s ease !important"
            leave-absolute
            hide-on-leave
            group
          >
            <v-text-field
              v-if="deviceSize.tablet || mobileSearch || search?.length"
              id="search"
              key="search"
              v-model="search"
              label="Search"
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              hide-details
              clearable
              style="width: clamp(75px, 80%, 90%)"
              @focus="handleSearchOpen"
              @blur="handleSearchClose"
            />
            <StyledButton
              v-if="
                !mobileSearch &&
                  $vuetify.breakpoint.smAndDown &&
                  !search?.length
              "
              key="searchButton"
              :action="handleSearchClick"
              preset="outlined-stacked"
              icon="mdi-magnify"
              :dense="true"
              label="Search"
              rounded="rounded"
              size="large"
            />
          </v-fade-transition>
        </div>
      </div>
      <v-expand-transition>
        <div
          v-if="showFilters"
          class="d-flex justify-space-around align-start pa-2 rounded"
          style="gap: 24px"
          :style="{
            border: `1px dotted ${$vuetify.theme.currentTheme.background3}`,
          }"
        >
          <span
            class="rounded-pill ps-2 pt-2 review--text text-caption d-flex align-center justify-center text-lg-body-2 font-weight-bold"
            style="height: 28px; gap: 6px"
          >
            <v-icon
              small
              color="review"
            > mdi-list-status </v-icon>
            <span>Status</span>
          </span>
          <div
            class="d-flex overflow-auto"
            :style="{ width: '100%', whiteSpace: 'nowrap' }"
          >
            <v-chip-group
              v-model="currentTab"
              active-class="review--text font-weight-black"
              class="text-caption font-weight-medium"
              :dark="$vuetify.theme.isDark"
              :light="!$vuetify.theme.isDark"
              :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
              show-arrows
            >
              <v-chip
                v-for="status in getOrderStatus"
                :key="status.value"
                small
                :outlined="currentTab === status.value"
                :value="status.value"
                :style="filterBackgroundColor"
              >
                {{ status.text }}
              </v-chip>
            </v-chip-group>
          </div>
        </div>
      </v-expand-transition>
      <v-data-table
        :key="searchTrigger"
        :headers="headers"
        :items="data"
        :dense="dense"
        :loading="parentLoading || loading"
        :items-per-page.sync="itemsPerPage"
        :search="effectiveSearch"
        :custom-filter="customSearch"
        :custom-sort="customSort"
        no-results-text="No results found"
        loading-text="Loading..."
        sort-by="date"
        sort-desc
        mobile-breakpoint="0"
        :page.sync="currentPage"
        fixed-header
        height="100%"
        class="d-flex flex-grow-1 flex-column"
        style="overflow: auto; max-height: 50vh"
        hide-default-footer
      >
        <template #item.number="{ item, index }">
          <span class="text-caption text-lg-body-2">
            {{ ((currentPage - 1) * itemsPerPage + index + 1) | addComma }}
          </span>
        </template>
        <template
          v-for="header in headers"
          #[`item.${header.value}`]="{ item, index }"
        >
          <span
            v-if="item[header.value]"
            class="text-caption text-lg-body-2"
          >{{
            item[header.value]
          }}</span>
          <span
            v-else-if="header.value === 'number'"
            class="text-caption text-lg-body-2"
          >{{
            ((currentPage - 1) * itemsPerPage + index + 1) | addComma
          }}</span>
          <span
            v-else-if="header.required"
            class="text-caption text-lg-body-2 error--text"
          >Not Set</span>
          <span
            v-else
            class="text-caption text-lg-body-2 text--disabled"
          >-</span>
        </template>
        <template #item.actions="{ item }">
          <div
            class="d-flex justify-center"
            style="gap: 8px"
          >
            <DelayedTooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  text
                  icon
                  v-bind="attrs"
                  :loading="item.loadingPreview"
                  color="review"
                  class="rounded"
                  style="min-width: 21px"
                  v-on="on"
                  @click.stop
                >
                  <v-icon small>
                    mdi-text-box-search
                  </v-icon>
                </v-btn>
              </template>
              Preview
            </DelayedTooltip>
            <router-link
              :to="{ name: 'salesOrder-view', params: { id: item.id } }"
              @click.stop
            >
              <DelayedTooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    small
                    text
                    icon
                    outlined
                    v-bind="attrs"
                    color="review"
                    class="rounded"
                    style="min-width: 21px"
                    v-on="on"
                  >
                    <v-icon small>
                      mdi-eye
                    </v-icon>
                  </v-btn>
                </template>
                View
              </DelayedTooltip>
            </router-link>
          </div>
        </template>
        <template #footer="{ props, on, headers, widths }">
          <v-divider class="d-block" />
          <div class="mt-3 d-flex justify-space-between align-center">
            <div
              v-if="deviceSize.tablet"
              class="d-flex justify-start align-center ml-2 flex-wrap"
              style="column-gap: 12px"
            >
              <span
                class="rounded-pill review--text text-caption d-flex align-center justify-center text-lg-body-2 font-weight-bold"
                style="height: 28px; gap: 6px"
              >
                <v-icon
                  small
                  color="review"
                > mdi-table-row-height </v-icon>
                <span>Rows:</span>
              </span>
              <v-chip-group
                v-model="itemsPerPage"
                active-class="review--text font-weight-black"
                class="text-caption font-weight-bold"
                mandatory
              >
                <v-chip
                  v-for="size in [5, 10, 15, 'All']"
                  :key="size"
                  small
                  :outlined="
                    itemsPerPage === size ||
                      (itemsPerPage === -1 && size === 'All')
                  "
                  :style="filterBackgroundColor"
                  :value="typeof size === 'number' ? size : -1"
                >
                  {{ size }}
                </v-chip>
              </v-chip-group>
            </div>
            <v-menu
              v-else
              offset-y
              :nudge-bottom="8"
              content-class="rounded-lg"
            >
              <template #activator="{ on, attrs, value }">
                <v-chip
                  :style="filterBackgroundColor"
                  v-bind="attrs"
                  close-icon="mdi-trash-can"
                  text-color="review"
                  class="rounded-pill text-caption text-lg-body-2 font-weight-bold"
                  style="padding-right: 10px"
                  v-on="on"
                  @click:close="currentTab = null"
                >
                  <div
                    class="d-flex align-center justify-center"
                    style="height: 28px; gap: 6px"
                  >
                    <v-icon
                      small
                      color="review"
                    >
                      mdi-table-row-height
                    </v-icon>
                    <div class="d-flex align-center justify-center">
                      <span class="mr-1">Rows:
                        <span class="font-weight-black">{{
                          itemsPerPage === -1 ? "All" : itemsPerPage
                        }}</span></span>
                      <v-icon
                        size="20"
                        color="review"
                        :class="{ rotate: value }"
                      >
                        mdi-menu-down
                      </v-icon>
                    </div>
                  </div>
                </v-chip>
              </template>
              <v-list
                dense
                content-class="rounded-lg"
              >
                <v-list-item
                  v-for="(item, index) in [5, 10, 15, 'All']"
                  :key="index"
                  :dense="$vuetify.breakpoint.smAndDown"
                  :style="{
                    minHeight: $vuetify.breakpoint.smAndDown ? '35px' : '40px',
                  }"
                  @click="itemsPerPage = item === 'All' ? -1 : item"
                >
                  <v-list-item-title
                    class="d-flex align-center"
                    :style="{
                      minHeight: $vuetify.breakpoint.smAndDown
                        ? '35px'
                        : '40px',
                    }"
                  >
                    {{ item }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div
              class="d-flex align-center"
              style="gap: 20px"
            >
              <span class="text-caption font-weight-medium text-end">{{
                `${$options.filters.addComma(
                  props.pagination.page * itemsPerPage - itemsPerPage + 1
                )}-${$options.filters.addComma(
                  itemsPerPage === -1
                    ? props.pagination.itemsLength
                    : Math.min(
                      props.pagination.itemsLength,
                      props.pagination.page * itemsPerPage
                    )
                )} of ${$options.filters.addComma(
                  props.pagination.itemsLength
                )}`
              }}</span>
              <div
                class="d-flex align-center"
                style="gap: 8px"
              >
                <v-btn
                  class="text-caption font-weight-bold"
                  :min-height="$vuetify.breakpoint.smAndDown ? '30px' : '40px'"
                  outlined
                  small
                  depressed
                  text
                  color="review"
                  style="text-transform: none !important"
                  :disabled="props.options.page - 1 === 0"
                  @click="props.options.page--"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  class="text-caption font-weight-bold"
                  :min-height="$vuetify.breakpoint.smAndDown ? '30px' : '40px'"
                  outlined
                  small
                  depressed
                  text
                  color="review"
                  style="text-transform: none !important"
                  :disabled="
                    props.options.page + 1 > props.pagination.pageCount
                  "
                  @click="props.options.page++"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </template>
      </v-data-table>
    </div>
  </v-fade-transition>
</template>

<script>
// Libraries
import { mapGetters } from "vuex";


// Components
import moment from "moment-timezone";

import StyledButton from "@/components/misc/ui/buttons/StyledButton.vue";

export default {
  name: "DefaultTable",
  components: {
    StyledButton,
  },
  props: {
    parentLoading: {
      default: false,
      type: Boolean,
      required: false,
    },
    dense: {
      default: false,
      type: Boolean,
      required: false,
    },
    headers: {
      default: () => [
        { text: "#", value: "number", align: "center", sortable: false },
        { text: "Name", value: "name" },
      ],
      type: Array,
      required: false,
    },
    queryData: {
      type: Array,
      required: true,
    },
    title: {
      default: "",
      type: String,
      required: false,
    },
    filterable: {
      default: false,
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    loading: false,
    data: [],
    snapshots: [],
    currentTab: "",
    search: "",
    showFilters: false,
    searchTrigger: 0,
    currentPage: 1,
    itemsPerPage: 5,
    mobileSearch: false,
    showFilters: false,
  }),
  watch: {
    currentTab() {
      this.searchTrigger++; // Increment trigger to force filter refresh
    },
  },
  async created() {
    await this.setup();
  },
  methods: {
    /* API Calls */
    // GET
    async getData() {
      this.loading = true;
      this.data = [];
      if (!this.queryData?.length) {
        this.loading = false;
        return;
      }

      for (const queryData of this.queryData) {
        try {
          let data = await this.$firebaseService.queryCollection(
            queryData.collection,
            queryData.conditions
          );

          data = data.map((doc) => {
            Object.keys(doc).forEach((key) => {
              let header = this.headers.find((header) => header.value === key);
              if (doc[key]?.hasOwnProperty("seconds")) {
                doc[key] = this.$options.filters.shortDateFormat(
                  doc[key].seconds * 1000
                );
              } else if (header?.format === "currency") {
                doc[key] = this.$options.filters.currency(doc[key]);
              } else if (header?.format === "number") {
                doc[key] = this.$options.filters.addComma(doc[key]);
              } else if (header?.values) {
                doc[key] = header.values.find(
                  (item) => item.value === doc[key]
                )?.text;
              } else if (
                ["createdBy", "name", "lastUpdated"].includes(key) &&
                typeof doc[key] === "object"
              ) {
                switch (key) {
                  case "lastUpdated":
                  case "createdBy":
                    doc[key] = this.$options.filters.capitalize(
                      `${doc[key].name.first} ${
                        header?.format === "firstLastInitial"
                          ? `${doc[key].name.last[0]}.`
                          : doc[key].name.last
                      }`
                    );
                    break;
                  case "name":
                    doc[key] = this.$options.filters.capitalize(
                      `${doc[key].first} ${
                        header?.format === "firstLastInitial"
                          ? `${doc[key].last[0]}.`
                          : doc[key].last
                      }`
                    );
                    break;
                  default:
                    break;
                }
              }
            });
            return {
              ...doc,
            };
          });
          this.data = this.data.concat(data);
        } catch (error) {
          console.log(error);
          this.data = [];
          this.$root.toast.show({
            color: "error",
            icon: "mdi-alert-circle",
            title: "Error processing request",
            subtitle: error.message,
          });
        }
      }

      this.loading = false;
    },
    // POST
    /* Main */
    async setup() {
      await this.getData();
    },
    customSearch(value, search, item) {
      let tempItem = JSON.parse(JSON.stringify(item));
      let statusMatch = this.currentTab
        ? item.status.value === this.currentTab
        : true;
      if (!this.search?.length) return statusMatch;
      tempItem.created = moment(tempItem.created).format("MM/DD/YYYY");
      tempItem.shipDate = moment(tempItem.shipDate).format("MM/DD/YYYY");
      tempItem.status = item.status.text;
      let itemMatch = Object.values(tempItem).some(
        (v) => v && String(v).toLowerCase().includes(this.search.toLowerCase())
      );
      if (!this.currentTab) return itemMatch;
      return statusMatch && itemMatch;
    },
    customSort(items, sortBy, sortDesc, ...others) {
      if (!sortBy.length) return items;
      let sortByField = sortBy[0];
      let sortDescField = sortDesc[0];
      return items.sort((a, b) => {
        let tempItem1 = JSON.parse(JSON.stringify(a));
        let tempItem2 = JSON.parse(JSON.stringify(b));
        tempItem1 = tempItem1[sortByField];
        tempItem2 = tempItem2[sortByField];
        if (sortDesc) {
          [tempItem1, tempItem2] = [tempItem2, tempItem1];
        }
        return tempItem1 > tempItem2 === sortDescField ? 1 : -1;
      });
    },
    /* Misc */
    handleSearchClick() {
      this.mobileSearch = true;
      this.$nextTick(() => {
        document.getElementById("search").focus();
      });
    },
    handleSearchOpen() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.showFilters = false;
      }
    },
    handleSearchClose() {
      this.mobileSearch = false;
    },
  },
  computed: {
    ...mapGetters([
      "getAppName",
      "getUser",
      "getOrderStatus",
      "getPaymentTerms",
    ]),
    effectiveSearch() {
      if (!this.currentTab) return this.search;
      return this.search + " | " + this.currentTab;
    },
    filterBackgroundColor() {
      return this.$vuetify.theme.isDark
        ? "background-color: var(--v-background-lighten1)"
        : "background-color: var(--v-background-base)";
    },
  },
};
</script>

<style scoped>
.rotate {
  transform: rotate(180deg);
}
.softShadow {
  box-shadow: rgba(149, 157, 165, 0.4) 0px 8px 24px !important;
}

.overflow-auto {
  overflow-x: scroll !important;
  scroll-behavior: smooth !important; /* Enables inertia-like scrolling */
  -webkit-overflow-scrolling: touch !important; /* Enables smooth scrolling on iOS */
}
</style>
